/**
 * 格式化工具函数
 */

/**
 * 格式化货币金额
 * @param amount 金额
 * @param currency 货币符号，默认为¥
 * @param decimals 小数位数，默认为2
 * @returns 格式化后的货币字符串
 */
export const formatCurrency = (amount: number, currency: string = '¥', decimals: number = 2): string => {
  if (amount === null || amount === undefined) return `${currency}0.00`
  
  const formattedAmount = Math.abs(amount).toFixed(decimals)
  const [integer, decimal] = formattedAmount.split('.')
  const integerWithCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  
  const sign = amount < 0 ? '-' : ''
  return `${sign}${currency}${integerWithCommas}${decimal ? `.${decimal}` : ''}`
}

/**
 * 格式化百分比
 * @param value 百分比值（如0.15表示15%）
 * @param decimals 小数位数，默认为1
 * @returns 格式化后的百分比字符串
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  if (value === null || value === undefined) return '0.0%'
  
  const percentValue = value * 100
  const formatted = Math.abs(percentValue).toFixed(decimals)
  const sign = percentValue < 0 ? '-' : percentValue > 0 ? '+' : ''
  
  return `${sign}${formatted}%`
}

/**
 * 格式化日期
 * @param date 日期字符串或Date对象
 * @param format 格式，默认为'YYYY-MM-DD'
 * @returns 格式化后的日期字符串
 */
export const formatDate = (date: string | Date, format: string = 'YYYY-MM-DD'): string => {
  if (!date) return ''
  
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  
  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`
    case 'YYYY年MM月DD日':
      return `${year}年${month}月${day}日`
    case 'MM-DD':
      return `${month}-${day}`
    case 'HH:mm':
      return `${hours}:${minutes}`
    case 'YYYY-MM-DD HH:mm':
      return `${year}-${month}-${day} ${hours}:${minutes}`
    case 'YYYY-MM-DD HH:mm:ss':
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    default:
      return `${year}-${month}-${day}`
  }
}

/**
 * 界面展示用日期时间：兼容接口 ISO 串（含 6 位小数秒）、Unix 毫秒时间戳等，输出 `YYYY-MM-DD HH:mm:ss`（本地时区）。
 * 无法解析为日期时返回原字符串（便于排查非标准格式）。
 */
export function formatDisplayDateTime(value: unknown, empty: string = '-'): string {
  if (value === null || value === undefined) return empty
  if (typeof value === 'number' && !Number.isNaN(value)) {
    const formatted = formatDate(new Date(value), 'YYYY-MM-DD HH:mm:ss')
    return formatted || empty
  }
  const raw = String(value).trim()
  if (raw === '') return empty
  // 小数秒超过 3 位时只保留毫秒，便于 `Date` 解析（如后端 Python 微秒）
  const normalized = raw.replace(/(\.\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2}|$))/i, '$1')
  const formatted = formatDate(normalized, 'YYYY-MM-DD HH:mm:ss')
  return formatted || raw || empty
}

/** 是否视为已上链（与 {@link formatOnChainLabel} 正向判定一致） */
export function isOnChained(raw: unknown): boolean {
  if (raw == null) return false
  const s = String(raw).trim()
  if (!s) return false
  const lower = s.toLowerCase()
  return s === '是' || s === '已上链' || ['y', 'yes', 'true', '1', 'on'].includes(lower)
}

/** 上链状态：接口 `onChain` 常见为「是 / 否」→ 已上链 / 未上链；无法识别时返回空串 */
export function formatOnChainLabel(raw: unknown): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (isOnChained(raw)) {
    return '已上链'
  }
  const lower = s.toLowerCase()
  if (s === '否' || s === '未上链' || ['n', 'no', 'false', '0', 'off'].includes(lower)) {
    return '未上链'
  }
  return ''
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @param decimals 小数位数，默认为2
 * @returns 格式化后的文件大小字符串
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
  
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * 格式化数字，添加千位分隔符
 * @param num 数字
 * @returns 格式化后的数字字符串
 */
export const formatNumber = (num: number): string => {
  if (num === null || num === undefined) return '0'
  
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * 格式化时间间隔
 * @param milliseconds 毫秒数
 * @returns 格式化后的时间间隔字符串
 */
export const formatDuration = (milliseconds: number): string => {
  if (milliseconds < 1000) {
    return `${milliseconds}ms`
  }
  
  const seconds = Math.floor(milliseconds / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }
  
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`
  }
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ${minutes % 60}m`
  }
  
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

/**
 * 截断文本
 * @param text 文本
 * @param maxLength 最大长度
 * @param suffix 后缀，默认为'...'
 * @returns 截断后的文本
 */
export const truncateText = (text: string, maxLength: number, suffix: string = '...'): string => {
  if (!text || text.length <= maxLength) return text
  
  return text.substring(0, maxLength) + suffix
}

/** 空值统一为「-」（与资产数据列表约定一致） */
export function strOrDash(...vals: unknown[]): string {
  for (const x of vals) {
    if (x != null && x !== '') return String(x)
  }
  return '-'
}

/** 解析资产数据项 ID（关联列表可能带 dataSourceId / 嵌套 dataSource） */
export function pickDataItemId(item: Record<string, unknown>): string {
  const nested = item.dataSource ?? item.data_source
  const src =
    nested != null && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : item
  for (const x of [
    item.dataSourceId,
    item.data_source_id,
    src.data_id,
    src.id,
    item.data_id,
    item.id,
  ]) {
    if (x != null && String(x).trim() !== '' && String(x).trim() !== '-') {
      return String(x).trim()
    }
  }
  return ''
}

export function pickListOrdernumber(item: Record<string, unknown>): string {
  const raw = String(
    item.ordernumber ??
      item.orderNumber ??
      item.order_number ??
      item.orderNo ??
      item.order_no ??
      item.FOrderNo ??
      item.forder_no ??
      '',
  ).trim()
  return raw || '-'
}

export function pickListBillNo(item: Record<string, unknown>): string {
  const raw = String(
    item.bill_no ??
      item.billNo ??
      item.BillNo ??
      item.documentNo ??
      item.document_no ??
      item.billNumber ??
      item.bill_number ??
      item.billno ??
      '',
  ).trim()
  return raw || '-'
}

/**
 * 获取风险等级颜色
 * @param risk 风险等级
 * @returns 对应的颜色类名
 */
export const getRiskColor = (risk: string): string => {
  const colorMap: Record<string, string> = {
    '低': 'green',
    '中低': 'blue',
    '中': 'orange',
    '中高': 'purple',
    '高': 'red',
  }
  
  return colorMap[risk] || 'gray'
}

/**
 * 获取状态颜色
 * @param status 状态
 * @returns 对应的颜色类名
 */
export const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'active': 'green',
    'pending': 'orange',
    'closed': 'red',
    'success': 'green',
    'warning': 'orange',
    'error': 'red',
    'info': 'blue',
  }
  
  return colorMap[status] || 'gray'
}

/**
 * 生成随机ID
 * @param length ID长度，默认为8
 * @returns 随机ID
 */
export const generateId = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  
  return result
}

/**
 * 防抖函数
 * @param func 要执行的函数
 * @param wait 等待时间（毫秒）
 * @returns 防抖后的函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * 节流函数
 * @param func 要执行的函数
 * @param limit 限制时间（毫秒）
 * @returns 节流后的函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}